// vite-plugin-vuetify compiles this import through src/styles/settings.scss
// (`styles.configFile` in vite.config.js) — keep exactly one such import app-wide.
import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import { createVuetify } from 'vuetify'
import { cs, en, de, pl, sk, hu } from 'vuetify/locale'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import { VStepperVertical, VStepperVerticalItem, VStepperVerticalActions } from 'vuetify/labs/VStepperVertical'

// Design system: __docs/engineering/design-system (MOB-514).
// `primary` is the single white-label branding knob — tenants override it here.
// `cta` is the conversion green (product-card CTA, add-to-cart, final order),
// `navy` the price chip over product photos, `hero` the landing headline band.
// Semantic colors are reserved for outcomes (success/warning/error), never decoration.
export const vuetifyOptions = {
  locale: {
    locale: 'cs',
    fallback: 'en',
    messages: { cs, en, de, pl, sk, hu },
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: { mdi },
  },
  components: {
    VStepperVertical,
    VStepperVerticalItem,
    VStepperVerticalActions,
  },
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        dark: false,
        colors: {
          primary: '#1976D2',
          'primary-darken-1': '#1565C0',
          'primary-container': '#E3F2FD',
          'on-primary-container': '#0D47A1',
          secondary: '#546E7A',
          cta: '#8BC34A',
          'cta-darken-1': '#7CB342',
          // rgba(0,0,0,.87) composited over the green — theme colors carry no alpha
          'on-cta': '#121909',
          navy: '#10365C',
          'on-navy': '#FFFFFF',
          // The shipped cyan, kept by product-owner decision (2026-08-19) although
          // white band text measures ~2.5:1 on it; the WCAG AA gap on the hero
          // band is accepted and stays documented in the MOB-514 critique.
          hero: '#00AAFF',
          'on-hero': '#FFFFFF',
          'hero-darken-1': '#0091D9',
          background: '#FFFFFF',
          surface: '#FFFFFF',
          'surface-variant': '#F5F5F5',
          'on-surface': '#2C3E50',
          'on-background': '#2C3E50',
          'on-surface-variant': '#546E7A',
          // Forest green, not Material #4CAF50: success now reads apart from the
          // committed lime CTA family, and passes AA as tonal-alert text (5.1:1
          // on white; the old value measured 3.0:1).
          success: '#2E7D32',
          // Deep amber, not #FB8C00: warning is the app's most used status (22
          // sites) and its text form measured 2.2:1 on white; this value is 4.5:1.
          warning: '#B45309',
          // #D32F2F, not the shipped #FF5252: validation text at 12px measured 3.19:1
          // on white and the AA target needs 4.5:1 (this red is 4.9:1).
          error: '#D32F2F',
          // Info folds into the primary blue: #2196F3 sat one step from #1976D2,
          // so informational alerts looked like a second, slightly-off action color.
          info: '#1976D2',
        },
        variables: {
          // black at 0.12 over white renders the DS rule color #E0E0E0 exactly
          'border-color': '#000000',
          'border-opacity': 0.12,
          // 0.74, not 0.62: composited over white the old value was 3.66:1 and the
          // role is used on 16px text (AA needs 4.5:1; 0.74 measures ~4.6:1).
          'medium-emphasis-opacity': 0.74,
          'disabled-opacity': 0.38,
          'hover-opacity': 0.08,
          'pressed-opacity': 0.16,
        },
      },
    },
  },
  defaults: {
    VAppBar: { color: 'surface', elevation: 1, height: 64 },
    VBtn: { color: 'primary', variant: 'elevated' },
    VAlert: { variant: 'tonal', density: 'comfortable' },
    VTextField: { variant: 'outlined', density: 'comfortable', color: 'primary' },
    VSelect: { variant: 'outlined', density: 'comfortable', color: 'primary' },
    VAutocomplete: { variant: 'outlined', density: 'comfortable', color: 'primary' },
    VCombobox: { variant: 'outlined', density: 'comfortable', color: 'primary' },
    VTextarea: { variant: 'outlined', density: 'comfortable', color: 'primary' },
    VCheckbox: { color: 'primary', density: 'comfortable' },
    VRadioGroup: { color: 'primary' },
    VSwitch: { color: 'primary' },
    VChip: { variant: 'tonal', label: true },
    VCard: { elevation: 2 },
    VDialog: { maxWidth: 480 },
    VTabs: { color: 'primary' },
    VDatePicker: { color: 'primary' },
  },
}

export default createVuetify(vuetifyOptions)
